'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Badge {
  id: number;
  icon: string;
  titleFr: string;
  titleDe: string;
  descriptionFr: string;
  descriptionDe: string;
}

interface TrustBadgesProps {
  className?: string;
}

const TrustBadges = ({ className = '' }: TrustBadgesProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('fr');

  useEffect(() => {
    setIsHydrated(true);
    const savedLanguage = localStorage.getItem('preferredLanguage') || 'fr';
    setCurrentLanguage(savedLanguage);
  }, []);

  const badges: Badge[] = [
    {
      id: 1,
      icon: 'ShieldCheckIcon',
      titleFr: 'Assurance complète',
      titleDe: 'Vollständige Versicherung',
      descriptionFr: 'Tous nos services sont couverts par une assurance responsabilité civile complète',
      descriptionDe: 'Alle unsere Dienstleistungen sind durch eine umfassende Haftpflichtversicherung abgedeckt',
    },
    {
      id: 2,
      icon: 'AcademicCapIcon',
      titleFr: 'Équipe certifiée',
      titleDe: 'Zertifiziertes Team',
      descriptionFr: 'Personnel formé et certifié selon les normes suisses de qualité',
      descriptionDe: 'Geschultes und zertifiziertes Personal nach Schweizer Qualitätsstandards',
    },
    {
      id: 3,
      icon: 'ClockIcon',
      titleFr: 'Disponibilité 24/7',
      titleDe: 'Verfügbarkeit 24/7',
      descriptionFr: 'Service client disponible à tout moment pour vos urgences',
      descriptionDe: 'Kundenservice jederzeit für Ihre Notfälle verfügbar',
    },
    {
      id: 4,
      icon: 'HandThumbUpIcon',
      titleFr: 'Garantie satisfaction',
      titleDe: 'Zufriedenheitsgarantie',
      descriptionFr: 'Si vous n\'êtes pas satisfait, nous revenons gratuitement',
      descriptionDe: 'Wenn Sie nicht zufrieden sind, kommen wir kostenlos zurück',
    },
    {
      id: 5,
      icon: 'CurrencyDollarIcon',
      titleFr: 'Prix transparents',
      titleDe: 'Transparente Preise',
      descriptionFr: 'Devis détaillés en CHF sans frais cachés',
      descriptionDe: 'Detaillierte Angebote in CHF ohne versteckte Kosten',
    },
    {
      id: 6,
      icon: 'SparklesIcon',
      titleFr: 'Produits écologiques',
      titleDe: 'Umweltfreundliche Produkte',
      descriptionFr: 'Utilisation de produits de nettoyage respectueux de l\'environnement',
      descriptionDe: 'Verwendung umweltfreundlicher Reinigungsprodukte',
    },
  ];

  const content = {
    fr: {
      title: 'Pourquoi choisir Services Rouissi',
      subtitle: 'Votre tranquillité d\'esprit est notre priorité',
    },
    de: {
      title: 'Warum Services Rouissi wählen',
      subtitle: 'Ihre Seelenruhe ist unsere Priorität',
    },
  };

  const currentContent = isHydrated ? content[currentLanguage as keyof typeof content] : content.fr;

  return (
    <section className={`py-16 lg:py-24 bg-background ${className}`}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            {currentContent.title}
          </h2>
          <p className="text-lg text-muted-foreground">
            {currentContent.subtitle}
          </p>
        </div>

        {/* Badges Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {badges.map((badge) => (
            <div
              key={badge.id}
              className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon
                    name={badge.icon as any}
                    size={24}
                    variant="solid"
                    className="text-primary"
                  />
                </div>
                <div>
                  <h3 className="text-lg font-heading font-bold text-foreground mb-2">
                    {isHydrated && currentLanguage === 'de' ? badge.titleDe : badge.titleFr}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {isHydrated && currentLanguage === 'de' ? badge.descriptionDe : badge.descriptionFr}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto mt-16">
          <div className="text-center">
            <div className="text-4xl lg:text-5xl font-heading font-bold text-primary mb-2">500+</div>
            <div className="text-sm text-muted-foreground">Clients satisfaits</div>
          </div>
          <div className="text-center">
            <div className="text-4xl lg:text-5xl font-heading font-bold text-primary mb-2">10+</div>
            <div className="text-sm text-muted-foreground">Années d'expérience</div>
          </div>
          <div className="text-center">
            <div className="text-4xl lg:text-5xl font-heading font-bold text-primary mb-2">100%</div>
            <div className="text-sm text-muted-foreground">Satisfaction garantie</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustBadges;